import { locationService } from '@website/services/location.service';

export class AddressGeocodeError extends Error {
  readonly code: 'ADDRESS_UNRESOLVED';

  constructor(message = 'ADDRESS_UNRESOLVED') {
    super(message);
    this.name = 'AddressGeocodeError';
    this.code = 'ADDRESS_UNRESOLVED';
  }
}

export interface ResolveAddressInput {
  addressLine1: string;
  city: string;
  zipCode?: string;
  country: string;
  latitude?: number;
  longitude?: number;
}

export interface ResolvedCoordinates {
  latitude: number;
  longitude: number;
}

/**
 * Resolve latitude/longitude for a postal address.
 *
 * Trusts existing coordinates when both are already set (e.g. the user picked a
 * location on the map). Otherwise it asks the backend geocoder and returns the
 * first match. Throws {@link AddressGeocodeError} when no match can be found so
 * the UI can block the save and surface a toast/field error to the user.
 */
export async function resolveAddressCoordinates(input: ResolveAddressInput): Promise<ResolvedCoordinates> {
  if (typeof input.latitude === 'number' && typeof input.longitude === 'number') {
    return { latitude: input.latitude, longitude: input.longitude };
  }

  const query = [input.addressLine1, input.city, input.zipCode, input.country]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

  if (query.length < 2) {
    throw new AddressGeocodeError();
  }

  const results = await locationService.searchLocations(query);
  const match = results[0];

  if (!match || typeof match.latitude !== 'number' || typeof match.longitude !== 'number') {
    throw new AddressGeocodeError();
  }

  return { latitude: match.latitude, longitude: match.longitude };
}
